import { useEffect, useState } from "react";
import { Button, Card, Col, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { CardImage } from "./CardImage";
import { TruncatedText } from "./TruncatedText";
import { File } from "@/backend/models/file";
import { I18n } from "@/i18n/I18n";
import { FileSubjectTypes } from "@/types/fileTypes";
import { Form } from "react-bootstrap";
import { ExpandableImage } from "../files/ExpandableImage";
import { EditImageButton } from "./EditImageButton";
import { DeleteButton } from "../buttons/DeleteButton";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { FusawarePermission } from "@/frontend/fusawareUser";
import { AlertTriangle, Pencil, LockKeyhole } from "lucide-react";
import { ProjectLock } from "@/backend/models/projectLock";

interface HierarchyModel {
  id: string;
  title: string;
  description: string;
  file?: File;
}

interface HierarchyCardProps<T extends HierarchyModel> {
  model: T;
  titleLink?: string;
  modelLabel: string;
  updateModel: (model: T) => Promise<any>;
  deleteModel?: (id: string) => Promise<any>;
  subjectType: FileSubjectTypes;
  refetchSubject: (id: string) => Promise<void>;
  renderChildren?: (fileSourceUrl: string | undefined) => React.ReactNode;
  viewOnly?: boolean;
  lock?: ProjectLock;
}

export function HierarchyCard<T extends HierarchyModel>(props: HierarchyCardProps<T>) {
  const { model, titleLink, modelLabel, updateModel, subjectType, refetchSubject, deleteModel, viewOnly, lock } = props;
  const userClass = useGlobalStore(state => state.userClass);
  const [fileSourceUrl, setFileSourceUrl] = useState<string | undefined>(undefined);

  return (
    <Col>
      <Card className="h-100 position-relative" style={{ overflow: 'hidden' }}>
        {lock && <LockBanner lock={lock} />}
        <div className={`${lock ? 'px-3 pb-3' : 'p-3'} bg-light`}>
          <div className="bg-white rounded d-flex align-items-center justify-content-center" style={lock?.isLocked() ? { filter: "grayscale(100%)" } : undefined}>
             <CardImage model={model} fileSourceUrl={fileSourceUrl} setFileSourceUrl={setFileSourceUrl} />
          </div>
        </div>
        <Card.Body className="d-flex flex-column bg-light">
          <Card.Title className="d-flex justify-content-between align-items-center">
            {titleLink && !lock?.isLocked() ? (
              <Link className="text-dark" to={titleLink}>{model.title}</Link>
            ) : (
              model.title
            )}
            {!viewOnly && !lock?.isLocked() && userClass?.fusaHasPermission(FusawarePermission.EDIT_HIERARCHY) && <div className="d-flex gap-2 h-100">
              <EditHierarchyButton model={model} modelLabel={modelLabel} fileSource={fileSourceUrl} updateModel={updateModel} subjectType={subjectType} refetchSubject={refetchSubject} />
              {deleteModel && <DeleteButton title={model.title} onDelete={() => deleteModel(model.id)} model={modelLabel} />}
            </div>}
          </Card.Title>
          <Card.Text className="text-muted">
            <TruncatedText text={model.description} maxLength={90} className="text-muted" />
          </Card.Text>
        </Card.Body>
        {lock?.isLocked() && (
          <>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "#87807b",
                mixBlendMode: "multiply",
                zIndex: 1,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
              }}
            >
              <LockKeyhole size={48} color="#fff" />
            </div>
          </>
        )}
      </Card> 
    </Col>
  );
}

function LockBanner(props: { lock: ProjectLock }) {
  const { lock } = props;
  if(lock.isLocked()) {
    return (
      <div
        className="d-flex align-items-center gap-2"
        style={{
          position: "relative",
          zIndex: 3,
          background: "var(--neutral-950)",
          padding: "8px 12px",
          color: "#fff",
          fontSize: "12px",
          lineHeight: 1.2,
        }}
      >
        <LockKeyhole size={16} color="#fff" style={{ flexShrink: 0 }} />
        <div>
          <p className="mb-0" style={{ fontWeight: 600 }}>
            {I18n.get("projectLocked")}
          </p>
          <p className="mb-0">
            {I18n.get("contactProjectManagerToUnlock")}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className="d-flex align-items-center gap-1"
      style={{
        background: "var(--red-600)",
        padding: "8px 12px",
        color: "#fff",
      }}
    >
      <AlertTriangle size={16} color="#fff" />
      <span style={{ fontSize: "12px", fontWeight: 600 }}>
        {I18n.get("projectWillLockOn", {
          date: new Date(lock!.lockDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        })}
      </span>
    </div>
  );
}

export function EditHierarchyButton<T extends HierarchyModel>(props: { model: T, modelLabel: string, fileSource?: string, updateModel: (model: T) => Promise<void>, subjectType: FileSubjectTypes, refetchSubject: (id: string) => Promise<void> }) {
  const { model, modelLabel, fileSource, updateModel, subjectType, refetchSubject } = props;
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState(model.title);
  const [description, setDescription] = useState(model.description || '');
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    if (showModal) {
      setTitle(model.title);
      setDescription(model.description || '');
      setTitleError('');
    }
  }, [showModal, model.title, model.description]);

  const handleUpdate = async () => {
    if (!title) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }
    setIsSubmitting(true);
    await updateModel({ ...model, title, description });
    setIsSubmitting(false);
    setShowModal(false);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setTitleError('');
  };

  return (
    <div>
      <Button size="sm" className="btn-blue-light" title={I18n.get('editModel', { model: modelLabel, title: model.title })} onClick={() => setShowModal(true)}>
        <Pencil size={16} />
      </Button>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('editHierarchyModel', { model: modelLabel })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
          <Form.Label>{I18n.get('title')}</Form.Label>
        <Form.Control
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder={I18n.get('enterField', { field: I18n.get('title') })}
          aria-label={I18n.get('enterField', { field: I18n.get('title') })}
          isInvalid={!!titleError}
        />
        <Form.Control.Feedback type="invalid">
          {titleError}
        </Form.Control.Feedback>
        <br />
        <Form.Label>{I18n.get('description')}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={I18n.get('enterField', { field: I18n.get('description') })}
          aria-label={I18n.get('enterField', { field: I18n.get('description') })}
        />
            {fileSource && <ExpandableImage src={fileSource} alt={model.file?.fileName || 'Image'} />}
            <EditImageButton model={model} refetchSubject={refetchSubject} subjectType={subjectType} />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSubmitting}>
            {I18n.get('cancel')}
          </Button>
          <Button variant="primary" onClick={() => void handleUpdate()} disabled={isSubmitting}>
            {isSubmitting ? I18n.get('saving') : I18n.get('update')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

